import { useState } from 'react';
import { useProfile } from '../../hooks/useApi';
import { ResidencyEntry, Country } from '../../types/api.types';
import styles from './ResidencyPage.module.scss';

const COUNTRIES: { value: Country; label: string }[] = [
    { value: 'US', label: 'United States' },
    { value: 'INDIA', label: 'India' },
    { value: 'CAN', label: 'Canada' },
];

function getCountryLabel(country: Country): string {
    return COUNTRIES.find((c) => c.value === country)?.label || country;
}

function getCountryClass(country: Country): string {
    switch (country) {
        case 'INDIA':
            return styles.cardIndia;
        case 'US':
            return styles.cardUs;
        case 'CAN':
            return styles.cardCanada;
    }
}

function getSegmentClass(country: Country): string {
    switch (country) {
        case 'INDIA':
            return styles.segmentIndia;
        case 'US':
            return styles.segmentUs;
        case 'CAN':
            return styles.segmentCanada;
    }
}

export function ResidencyPage() {
    const { profile, loading, error, refetch, updateProfile } = useProfile();
    const [showModal, setShowModal] = useState(false);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);

    const entries = profile?.residencyHistory || [];

    const handleAdd = () => {
        setEditingIndex(null);
        setShowModal(true);
    };

    const handleEdit = (index: number) => {
        setEditingIndex(index);
        setShowModal(true);
    };

    const handleSave = async (entry: ResidencyEntry) => {
        const newEntries = [...entries];
        if (editingIndex !== null) {
            newEntries[editingIndex] = entry;
        } else {
            newEntries.push(entry);
        }
        newEntries.sort((a, b) => a.startAge - b.startAge);
        await updateProfile({ residencyHistory: newEntries });
        setShowModal(false);
        refetch();
    };

    const handleDelete = async (index: number) => {
        const newEntries = entries.filter((_, i) => i !== index);
        await updateProfile({ residencyHistory: newEntries });
        refetch();
    };

    if (loading) {
        return (
            <div className={styles.page}>
                <p className={styles.loading}>Loading residency timeline...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.page}>
                <p className={styles.error}>{error}</p>
                <button className={styles.retryButton} onClick={refetch}>
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <div>
                    <h1 className={styles.title}>Residency Timeline</h1>
                    <p className={styles.subtitle}>Map which countries you plan to live in at different ages.</p>
                </div>
                <button className={styles.addButton} onClick={handleAdd}>
                    + Add Period
                </button>
            </div>

            {entries.length > 0 && <TimelineBar entries={entries} currentAge={profile?.currentAge || 30} />}

            {entries.length === 0 ? (
                <div className={styles.emptyState}>
                    <p>No residency periods defined. Add your first country period to start planning.</p>
                    <button className={styles.addButton} onClick={handleAdd}>
                        + Add your first period
                    </button>
                </div>
            ) : (
                <div className={styles.entryList}>
                    {entries.map((entry, index) => (
                        <EntryCard
                            key={`${entry.country}-${entry.startAge}`}
                            entry={entry}
                            onEdit={() => handleEdit(index)}
                            onDelete={() => handleDelete(index)}
                        />
                    ))}
                </div>
            )}

            {showModal && (
                <AddEditModal
                    existingEntry={editingIndex !== null ? entries[editingIndex] : null}
                    entries={entries}
                    editingIndex={editingIndex}
                    currentAge={profile?.currentAge || 30}
                    currentCountry={entries.length > 0 ? entries[entries.length - 1].country : 'INDIA'}
                    onSave={handleSave}
                    onClose={() => setShowModal(false)}
                />
            )}
        </div>
    );
}

function TimelineBar({ entries, currentAge }: { entries: ResidencyEntry[]; currentAge: number }) {
    const timelineEnd = Math.max(95, ...entries.map((e) => e.endAge));
    const totalSpan = timelineEnd - currentAge;

    const ticks: number[] = [];
    for (let age = currentAge; age <= timelineEnd; age += 5) {
        ticks.push(age);
    }
    if (!ticks.includes(timelineEnd)) ticks.push(timelineEnd);

    return (
        <div className={styles.timelineSection}>
            <div className={styles.timelineBar}>
                {entries.map((entry, i) => (
                    <div
                        key={i}
                        className={`${styles.timelineSegment} ${getSegmentClass(entry.country)}`}
                        style={{
                            left: `${((entry.startAge - currentAge) / totalSpan) * 100}%`,
                            width: `${((entry.endAge - entry.startAge) / totalSpan) * 100}%`,
                        }}
                        title={`${getCountryLabel(entry.country)}: Age ${entry.startAge}–${entry.endAge}`}
                    >
                        {entry.endAge - entry.startAge >= 5 && getCountryLabel(entry.country)}
                    </div>
                ))}
            </div>
            <div className={styles.timelineAxis}>
                {ticks.map((age) => (
                    <span
                        key={age}
                        className={styles.axisTick}
                        style={{ left: `${((age - currentAge) / totalSpan) * 100}%` }}
                    >
                        {age}
                    </span>
                ))}
            </div>
            <div className={styles.legend}>
                <span className={styles.legendItem}>
                    <span className={`${styles.legendDot} ${styles.dotIndia}`} />
                    India
                </span>
                <span className={styles.legendItem}>
                    <span className={`${styles.legendDot} ${styles.dotUs}`} />
                    United States
                </span>
                <span className={styles.legendItem}>
                    <span className={`${styles.legendDot} ${styles.dotCanada}`} />
                    Canada
                </span>
            </div>
        </div>
    );
}

function EntryCard({ entry, onEdit, onDelete }: { entry: ResidencyEntry; onEdit: () => void; onDelete: () => void }) {
    return (
        <div className={`${styles.card} ${getCountryClass(entry.country)}`}>
            <div className={styles.cardHeader}>
                <span className={styles.cardCountryBadge}>{getCountryLabel(entry.country)}</span>
                <span className={styles.cardAgeRange}>
                    Age {entry.startAge} – {entry.endAge}
                </span>
            </div>
            <span className={styles.cardDuration}>{entry.endAge - entry.startAge} years</span>
            <div className={styles.cardActions}>
                <button className={styles.editButton} onClick={onEdit}>
                    Edit
                </button>
                <button className={styles.deleteButton} onClick={onDelete}>
                    Remove
                </button>
            </div>
        </div>
    );
}

interface AddEditModalProps {
    existingEntry: ResidencyEntry | null;
    entries: ResidencyEntry[];
    editingIndex: number | null;
    currentAge: number;
    currentCountry: Country;
    onSave: (entry: ResidencyEntry) => Promise<void>;
    onClose: () => void;
}

function AddEditModal({
    existingEntry,
    entries,
    editingIndex,
    currentAge,
    currentCountry,
    onSave,
    onClose,
}: AddEditModalProps) {
    const [startAge, setStartAge] = useState(String(existingEntry?.startAge || currentAge));
    const [endAge, setEndAge] = useState(String(existingEntry?.endAge || currentAge + 10));
    const [country, setCountry] = useState<Country>(existingEntry?.country || currentCountry);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    const validate = (): string | null => {
        const s = Number(startAge);
        const e = Number(endAge);
        if (isNaN(s) || isNaN(e)) return 'Please enter valid ages';
        if (s >= e) return 'Start age must be less than end age';
        if (s < currentAge) return `Start age cannot be before current age (${currentAge})`;
        if (e > 95) return 'End age cannot exceed 95';

        const otherEntries = entries.filter((_, i) => i !== editingIndex);
        for (const other of otherEntries) {
            if (s < other.endAge && e > other.startAge) {
                return `Overlaps with ${getCountryLabel(other.country)} period (age ${other.startAge}–${other.endAge})`;
            }
        }
        return null;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const validationError = validate();
        if (validationError) {
            setFormError(validationError);
            return;
        }
        setSaving(true);
        setFormError(null);
        try {
            await onSave({ startAge: Number(startAge), endAge: Number(endAge), country });
        } catch (err) {
            setFormError(err instanceof Error ? err.message : 'Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <h2>{existingEntry ? 'Edit Period' : 'Add Period'}</h2>
                    <button className={styles.closeButton} onClick={onClose}>
                        &times;
                    </button>
                </div>
                <form className={styles.form} onSubmit={handleSubmit}>
                    <div className={styles.field}>
                        <label className={styles.label}>Country</label>
                        <select
                            className={styles.input}
                            value={country}
                            onChange={(e) => setCountry(e.target.value as Country)}
                        >
                            {COUNTRIES.map((c) => (
                                <option key={c.value} value={c.value}>
                                    {c.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.row}>
                        <div className={styles.field}>
                            <label className={styles.label}>From Age</label>
                            <input
                                type="number"
                                className={styles.input}
                                value={startAge}
                                onChange={(e) => setStartAge(e.target.value)}
                                min={currentAge}
                                max={94}
                                required
                            />
                        </div>
                        <div className={styles.field}>
                            <label className={styles.label}>To Age</label>
                            <input
                                type="number"
                                className={styles.input}
                                value={endAge}
                                onChange={(e) => setEndAge(e.target.value)}
                                min={Number(startAge) + 1}
                                max={95}
                                required
                            />
                        </div>
                    </div>

                    {formError && <p className={styles.formError}>{formError}</p>}

                    <div className={styles.actions}>
                        <button type="button" className={styles.cancelButton} onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className={styles.submitButton} disabled={saving}>
                            {saving ? 'Saving...' : existingEntry ? 'Update' : 'Add Period'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
